import type { FC, ReactNode } from 'react'
import type { ServiceId, VehicleClassId } from '@/types'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { SectionLabel } from '@/components/generals/section-label'
import { CallbackDialog } from '@/components/landing/callback-dialog'
import { QuoteTicket } from '@/components/landing/quote-ticket'
import { formatEuro } from '@/lib/format'
import { computeEstimate, toggleService } from '@/lib/pricing'

import {
    DEFAULT_SERVICES,
    DEFAULT_VEHICLE_CLASS,
    SERVICES,
    VEHICLE_CLASSES
} from '@/constants/pricing'

export type EstimatePreset = {
    id: ServiceId
    nonce: number
}

interface QuoteSimulatorProps {
    preset: EstimatePreset | null
}

const OPTION_FOCUS = 'has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-ring'

export const QuoteSimulator: FC<QuoteSimulatorProps> = ({ preset }): ReactNode => {
    const [vehicleClass, setVehicleClass] = useState<VehicleClassId>(DEFAULT_VEHICLE_CLASS)
    const [selected, setSelected] = useState<ServiceId[]>(DEFAULT_SERVICES)
    const [dialogOpen, setDialogOpen] = useState(false)

    // Un clic sur « Estimer » dans la grille ajoute l'intervention correspondante.
    useEffect(() => {
        if (!preset) return

        setSelected(current => current.includes(preset.id) ? current : toggleService(current, preset.id))
    }, [preset])

    const estimate = useMemo(() => computeEstimate(vehicleClass, selected), [vehicleClass, selected])
    const vehicle = VEHICLE_CLASSES.find(item => item.id === vehicleClass) ?? VEHICLE_CLASSES[0]
    const closeDialog = useCallback(() => setDialogOpen(false), [])

    return (
        <section id='devis' aria-labelledby='devis-title' className='bg-surface py-20 lg:py-32'>
            <Container>
                <div className='grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-10 xl:gap-16'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='02'>Devis express</SectionLabel>

                        <h2 id='devis-title' className='heading mt-5 text-title'>
                            Estimez la facture avant même de venir.
                        </h2>
                    </div>

                    <p className='text-lead text-muted-foreground lg:col-span-5'>
                        Votre type de véhicule, les interventions, et vous voyez le détail pièces et main-d’œuvre, exactement comme sur notre devis papier.
                    </p>
                </div>

                <div className='mt-12 grid gap-10 lg:mt-16 lg:grid-cols-12 lg:gap-10 xl:gap-16'>
                    <div className='space-y-10 lg:col-span-7'>
                        <fieldset>
                            <legend className='flex items-baseline gap-3'>
                                <span className='label-mono text-signal-text'>Étape 1</span>
                                <span className='text-lg font-semibold'>Votre véhicule</span>
                            </legend>

                            <div className='mt-4 grid grid-cols-2 gap-3'>
                                {VEHICLE_CLASSES.map(item => {
                                    const active = item.id === vehicleClass

                                    return (
                                        <label
                                            key={item.id}
                                            className={cn(
                                                'flex min-h-[5.25rem] cursor-pointer flex-col justify-center rounded-lg border px-4 py-3 transition-colors duration-150',
                                                active ? 'border-foreground bg-card shadow-card' : 'border-border bg-card/50 hover:border-foreground/40',
                                                OPTION_FOCUS
                                            )}
                                        >
                                            <input
                                                type='radio'
                                                name='gabarit'
                                                value={item.id}
                                                checked={active}
                                                onChange={() => setVehicleClass(item.id)}
                                                className='sr-only'
                                            />

                                            <span className='flex items-center justify-between gap-2'>
                                                <span className='font-semibold leading-snug'>
                                                    {item.label}
                                                </span>

                                                <span
                                                    aria-hidden='true'
                                                    className={cn(
                                                        'size-5 shrink-0 rounded-full border transition-colors duration-150',
                                                        active ? 'border-[6px] border-ink' : 'border-input bg-card'
                                                    )}
                                                />
                                            </span>

                                            <span className='mt-1 text-sm leading-snug text-muted-foreground'>
                                                {item.examples}
                                            </span>
                                        </label>
                                    )
                                })}
                            </div>
                        </fieldset>

                        <fieldset>
                            <legend className='flex items-baseline gap-3'>
                                <span className='label-mono text-signal-text'>Étape 2</span>
                                <span className='text-lg font-semibold'>Les interventions</span>
                            </legend>

                            <ul className='mt-4 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card'>
                                {SERVICES.map(service => {
                                    const checked = selected.includes(service.id)
                                    const price = service.prices[vehicleClass]

                                    return (
                                        <li key={service.id}>
                                            <label
                                                className={cn(
                                                    'flex min-h-16 cursor-pointer items-center gap-4 px-4 py-3.5 transition-colors duration-150 hover:bg-foreground/[0.025]',
                                                    'has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-ring',
                                                    checked && 'bg-foreground/[0.02]'
                                                )}
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={checked}
                                                    onChange={() => setSelected(current => toggleService(current, service.id))}
                                                    className='sr-only'
                                                />

                                                <span
                                                    aria-hidden='true'
                                                    className={cn(
                                                        'grid size-6 shrink-0 place-items-center rounded-[5px] border transition-colors duration-150',
                                                        checked ? 'border-ink bg-ink text-chalk' : 'border-input bg-card'
                                                    )}
                                                >
                                                    {checked && <Check className='size-4' strokeWidth={3} />}
                                                </span>

                                                <span className='min-w-0 flex-1'>
                                                    <span className='block font-semibold leading-snug'>
                                                        {service.name}
                                                    </span>

                                                    <span className='mt-0.5 block text-sm leading-snug text-muted-foreground'>
                                                        {service.summary}
                                                    </span>
                                                </span>

                                                <span className='shrink-0 text-right font-mono text-sm font-medium tabular-nums'>
                                                    {service.free ? 'Offert' : formatEuro(price.ttc)}
                                                </span>
                                            </label>
                                        </li>
                                    )
                                })}
                            </ul>

                            <p className='mt-3 text-sm text-muted-foreground'>
                                Plaquettes seules ou disques et plaquettes&nbsp;: cocher l’un décoche l’autre.
                            </p>
                        </fieldset>
                    </div>

                    <div className='lg:col-span-5'>
                        <div className='lg:sticky lg:top-28'>
                            <QuoteTicket
                                estimate={estimate}
                                vehicleLabel={vehicle.label}
                                onCallback={() => setDialogOpen(true)}
                            />
                        </div>
                    </div>
                </div>
            </Container>

            <CallbackDialog
                open={dialogOpen}
                onClose={closeDialog}
                estimate={estimate}
                vehicleLabel={vehicle.label}
            />
        </section>
    )
}
